import './MarketplaceSettings.scss';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import {
    LoadMarketplacePricing,
    LoadMarketplaceTemplatePricing,
} from '../../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import { FormGroup, Col, Row } from 'reactstrap';
import '@availity/yup';
import * as yup from 'yup';
import { Field, Form } from '@availity/form';
import { FormikErrors, FormikProps } from 'formik';
import Helpers from '../../../../utils/helper';
import ConfirmationDialogWithReason from '../../../../components/ConfirmationDialog/confirmation-dialog-with-reason';
import { MarketplacePricing } from '../../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import { TransactionFees } from '../../../../slices/marketplace/pricing/models/transaction-fees.model';
import createNotification from '../../../../utils/createNotification';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { FeeCalculatorSettings } from '../../../../slices/marketplace/pricing/models/fee-calculator-settings.model';
import { BrokeragePricing } from '../../../../slices/marketplace/pricing/models/brokerage-pricing.model';
import TransactionFeesTable from './TransactionFeesTable/TransactionFeesTable';
import { FeeRefundStatus } from '../../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import TransactionFeeScheduleTable from './TransactionFeeScheduleTable/TransactionFeeScheduleTable';
import { MarketplacePercentField } from './MarketplaceSettingsFields/MarketplacePercentField';
import { MarketplaceCurrencyField } from './MarketplaceSettingsFields/MarketplaceCurrencyField';
import BuyersFeesTable from './BuyersFeesTable/BuyersFeesTable';
import { BuyersFees } from '../../../../slices/marketplace/pricing/models/buyers-fees-model';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';

export interface MarketplaceSettingsProps {
    isTemplate: boolean;
    engagementId?: number;
    engagementLevelOfService?: LevelOfService;
}

const MarketplaceSettings = (props: MarketplaceSettingsProps) => {
    let modalForm = useRef<FormikProps<MarketplacePricing>>(null);
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

    const [settings, setSettings] = useState<MarketplacePricing>();
    const [formInitialValues, setFormInitialValues] =
        useState<MarketplacePricing>(undefined);
    const [validationSchema, setValidationSchema] =
        useState<yup.AnyObjectSchema>();

    useEffect(() => {
        setIsLoading(true);
        fetchPricing().finally(() => setIsLoading(false));
    }, []);

    const fetchPricing = async () => {
        const response = props.isTemplate
            ? await dispatch(LoadMarketplaceTemplatePricing())
            : await dispatch(LoadMarketplacePricing(props.engagementId));
        let pricingCopy: MarketplacePricing = JSON.parse(
            JSON.stringify(response.payload.data)
        );
        setSettings(pricingCopy);
        setFormInitialValues(pricingCopy);
    };

    useEffect(() => {
        if (settings && !validationSchema) {
            setValidationSchema(getDefaultValidationSchema());
        }
    }, [settings]);

    const getDefaultValidationSchema = () => {
        const numberIntField = yup.number().required().min(1, 'min 1');
        const numberFloatField = yup.number().required().min(0.1, 'min 0.1');
        const percentField = yup
            .string()
            .required()
            .test('len', 'Must be in 0..100', (val) => {
                return (
                    Number(val?.replace('%', '')) >= 0 &&
                    Number(val?.replace('%', '')) <= 100
                );
            });
        const refundStatusField = yup
            .number()
            .required()
            .min(FeeRefundStatus.NonRefundable)
            .max(FeeRefundStatus.Partially);

        const feeCalculatorSettingsSchema = {
            innerRevenueMultiplePercent: percentField,
            innerValuePerDvmPercent: percentField,
            outerRevenueMultiplePercent: percentField,
            outerValuePerDvmPercent: percentField,
            middleOfRangeRevenueMultiple: numberFloatField,
            middleOfRangeValuePerDvm: numberIntField,
        };

        const transactionFeesSchema = {
            idNdaFeePerBuyer: numberIntField,
            idNdaFirstXCount: numberIntField,
            idNdaFeeRefundable: refundStatusField,
            idNdaFeeCreditable: percentField,
            dataNdaFeePerBuyer: numberIntField,
            dataNdaFirstXCount: numberIntField,
            dataNdaFeeRefundable: refundStatusField,
            dataNdaFeeCreditable: percentField,
            financialModelFeePerBuyer: numberIntField,
            financialModelFirstXCount: numberIntField,
            financialModelFeeRefundable: refundStatusField,
            financialModelFeeCreditable: percentField,
            buyerMeetingVirtualCredit: numberIntField,
            buyerMeetingInPersonCredit: numberIntField,
            buyerMeetingRefundable: refundStatusField,
            buyerMeetingCreditable: percentField,
            applicationFee: numberIntField,
            applicationFeePerDvm: numberIntField,
            applicationFeeRefundable: refundStatusField,
            applicationFeeCreditable: percentField,
            lateFeeFirstOccurrence: numberIntField,
            lateFeeAdditionalOccurrence: numberIntField,
            lateFeeRefundable: refundStatusField,
            tempoChangeFeePerDvm: numberIntField,
            //remove if clarified by Matt
            tempoChangeFeeRefundable: refundStatusField,
            tempoChangeFeeCreditable: yup
                .string()
                .required()
                .test('len', 'Must be in 0..100', (val) => {
                    return (
                        Number(val?.replace('%', '')) >= 0 &&
                        Number(val?.replace('%', '')) <= 100
                    );
                }),
            //
            tailPeriodMonths: yup
                .string()
                .required()
                .test('len', 'Must be more than 0', (val) => {
                    return Number(val?.replace(' months', '')) > 0;
                }),
        };

        return yup.object().shape({
            requestPreorderAgreementButtonText: yup.string().required(),
            buyersFees: yup.object().shape({
                sellerIdFeePerDvm: numberIntField,
                sellerIdFeeRefundable: refundStatusField,
                pipAccessFeePerDvm: numberIntField,
                pipAccessFeeRefundable: refundStatusField,
                dfmAccessFeePerDvm: numberIntField,
                dfmAccessFeeRefundable: refundStatusField,
                sellerMeetingFeeVirtual: numberIntField,
                sellerMeetingFeeInPerson: numberIntField,
                sellerMeetingFeeRefundable: refundStatusField,
            }),
            discountBrokeragePricing: yup.object({
                feeCalculatorSettings: yup
                    .object()
                    .shape(feeCalculatorSettingsSchema),
                transactionFees: yup.object().shape({
                    ...transactionFeesSchema,
                    suspensionFeeRefundable: refundStatusField,
                    suspensionFeeCreditable: percentField,
                }),
                transactionFeeSchedule: yup.array().of(
                    yup.object().shape({
                        revenueMax: numberIntField,
                        feePercent: percentField,
                    })
                ),
            }),
            fullServiceBrokeragePricing: yup.object({
                feeCalculatorSettings: yup
                    .object()
                    .shape(feeCalculatorSettingsSchema),
                transactionFees: yup.object().shape({
                    ...transactionFeesSchema,
                    monthlyRetainerFee: numberIntField,
                    monthlyRetainerPerDvm: numberIntField,
                    monthlyRetainerFeeRefundable: refundStatusField,
                    monthlyRetainerFeeCreditable: percentField,
                    suspensionFee: numberIntField,
                    suspensionFeePerDvm: numberIntField,
                }),
                transactionFeeSchedule: yup.array().of(
                    yup.object().shape({
                        revenueMax: numberIntField,
                        feePercent: percentField,
                    })
                ),
            }),
        });
    };

    const updateTable = (
        brokerageType:
            | 'discountBrokeragePricing'
            | 'fullServiceBrokeragePricing'
            | undefined,
        table: 'feeCalculatorSettings' | 'transactionFees' | 'buyersFees',
        newState: FeeCalculatorSettings | TransactionFees | BuyersFees
    ) => {
        if (table !== 'buyersFees') {
            let newPricing: BrokeragePricing =
                brokerageType === 'discountBrokeragePricing'
                    ? { ...settings.discountBrokeragePricing }
                    : { ...settings.fullServiceBrokeragePricing };

            switch (table) {
                case 'feeCalculatorSettings':
                    newPricing.feeCalculatorSettings =
                        newState as FeeCalculatorSettings;
                    break;
                case 'transactionFees':
                    newPricing.transactionFees = newState as TransactionFees;
                    break;
            }

            const newSettings: MarketplacePricing = {
                ...settings,
                [brokerageType]: newPricing,
            };
            setSettings(newSettings);
        } else {
            const newSettings: MarketplacePricing = {
                ...settings,
                buyersFees: newState as BuyersFees,
            };
            setSettings(newSettings);
        }
    };

    const initSubmit = () => {
        let discountFeeSchedule =
            modalForm.current.values.discountBrokeragePricing
                .transactionFeeSchedule;
        discountFeeSchedule[discountFeeSchedule.length - 1].revenueMax =
            discountFeeSchedule[discountFeeSchedule.length - 2].revenueMax;
        const discountSettings = { ...settings.discountBrokeragePricing };
        discountSettings.transactionFeeSchedule = discountFeeSchedule;

        const fullServiceSettings = { ...settings.fullServiceBrokeragePricing };
        let fsFeeSchedule =
            modalForm.current.values.fullServiceBrokeragePricing
                .transactionFeeSchedule;
        fsFeeSchedule[fsFeeSchedule.length - 1].revenueMax =
            fsFeeSchedule[fsFeeSchedule.length - 2].revenueMax;
        fullServiceSettings.transactionFeeSchedule = fsFeeSchedule;

        const buyersFees = { ...settings.buyersFees };

        const newSettings: MarketplacePricing = {
            ...settings,
            discountBrokeragePricing: discountSettings,
            fullServiceBrokeragePricing: fullServiceSettings,
            buyersFees: buyersFees,
        };
        setSettings(newSettings);
        modalForm.current.handleSubmit();
    };

    const saveChanges = () => {
        setIsSaving(true);
        axios
            .post('api/marketplace/pricing', settings)
            .then(async (response: any) => {
                if (response.response?.status === 403) {
                    createNotification(
                        `Marketplace Settings update failed due to concurrent updates. Please refresh page and try again.`,
                        'error'
                    );
                } else if (response.status === 200) {
                    createNotification(
                        'Marketplace Settings has been successfully updated.',
                        'success'
                    );
                    fetchPricing();
                } else {
                    createNotification(
                        `Marketplace Settings update failed due to unexpected error Please refresh page and try again.`,
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    `Marketplace Settings update failed due to unexpected error Please refresh page and try again.`,
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const updateTransactionFeeScheduleState = (
        target: string,
        rowAddedIndex: number,
        rowDeletedIndex: number,
        values: any
    ) => {
        const form = modalForm as any;
        if (
            (target && !form.current.touched[target]?.transactionFeeSchedule) ||
            (!rowAddedIndex && !rowDeletedIndex)
        )
            return;
        let touchedState = JSON.parse(
            JSON.stringify(form.current.touched[target])
        );
        if (rowDeletedIndex) {
            touchedState.transactionFeeSchedule.splice(rowDeletedIndex, 1);
            modalForm.current.setTouched(
                Object.assign(modalForm.current.touched, {
                    [target]: touchedState,
                })
            );
        } else if (
            rowAddedIndex &&
            values[target].transactionFeeSchedule.length - 1 > rowAddedIndex
        ) {
            let newRowTouchedState =
                touchedState.transactionFeeSchedule.length > rowAddedIndex
                    ? touchedState.transactionFeeSchedule[rowAddedIndex]
                    : null;

            touchedState.transactionFeeSchedule.splice(
                rowAddedIndex + 1,
                0,
                newRowTouchedState
            );
            touchedState.transactionFeeSchedule[rowAddedIndex] = null;
            modalForm.current.setTouched(
                Object.assign(modalForm.current.touched, {
                    [target]: touchedState,
                })
            );
        }
    };

    return (
        <div
            className={
                props.isTemplate
                    ? 'marketplace-settings mt15'
                    : 'marketplace-settings body_inner_content'
            }>
            <div>
                <div className="mb-3">
                    <strong>Marketplace Settings</strong>
                </div>
                <div
                    role="tabpanel"
                    id="panel-0"
                    aria-labelledby="tab-0"
                    aria-hidden="false">
                    <div className="card-body">
                        {!isLoading && settings ? (
                            <div>
                                <Form
                                    onSubmit={() => {
                                        setIsConfirmationVisible(true);
                                    }}
                                    initialValues={formInitialValues}
                                    validationSchema={validationSchema}
                                    innerRef={modalForm}>
                                    {({ values, setFieldValue }) => {
                                        return (
                                            <>
                                                <div className="body_inner_content form-content">
                                                    {props.isTemplate && (
                                                        <FormGroup>
                                                            <Row>
                                                                <Col md={2}>
                                                                    <label>
                                                                        Request
                                                                        Pre-Order
                                                                        Engagement
                                                                        Agreement
                                                                        button
                                                                        text
                                                                    </label>
                                                                </Col>
                                                                <Col
                                                                    md={8}
                                                                    className="right-padding-5">
                                                                    <Field
                                                                        type="text"
                                                                        name="requestPreorderAgreementButtonText"
                                                                        value={
                                                                            settings.requestPreorderAgreementButtonText
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) => {
                                                                            const val =
                                                                                event
                                                                                    .target
                                                                                    .value;

                                                                            const newSettings =
                                                                                {
                                                                                    ...settings,
                                                                                    requestPreorderAgreementButtonText:
                                                                                        val,
                                                                                };

                                                                            setSettings(
                                                                                newSettings
                                                                            );
                                                                        }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </FormGroup>
                                                    )}

                                                    <div className="transaction-fee-schedule-subsection">
                                                        <div>
                                                            <h6>
                                                                Transaction Fee
                                                                Schedule
                                                            </h6>
                                                        </div>
                                                        <div className="fee-section">
                                                            <TransactionFeeScheduleTable
                                                                pricing={values}
                                                                setValue={(
                                                                    field: string,
                                                                    value: any,
                                                                    shouldValidate?: boolean,
                                                                    rowAddedIndex?: number,
                                                                    rowDeletedIndex?: number
                                                                ): Promise<void | FormikErrors<MarketplacePricing>> => {
                                                                    const target =
                                                                        field.split(
                                                                            '.'
                                                                        )[0];
                                                                    return setFieldValue(
                                                                        field,
                                                                        value,
                                                                        shouldValidate
                                                                    ).then(
                                                                        () => {
                                                                            updateTransactionFeeScheduleState(
                                                                                target,
                                                                                rowAddedIndex,
                                                                                rowDeletedIndex,
                                                                                values
                                                                            );
                                                                        }
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <h6>
                                                            Fee Calculator
                                                            Settings
                                                        </h6>
                                                    </div>
                                                    <div className="fee-section">
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.DiscountBrokerage) && (
                                                            <>
                                                                <FormGroup>
                                                                    <Row className="center-items">
                                                                        <Col
                                                                            md={
                                                                                2
                                                                            }>
                                                                            {' '}
                                                                            <div>
                                                                                <label className="bold-label">
                                                                                    Discount
                                                                                    Brokerage
                                                                                </label>
                                                                            </div>
                                                                        </Col>
                                                                        <Col
                                                                            md={
                                                                                6
                                                                            }>
                                                                            <div className="fee-section">
                                                                                <Row>
                                                                                    <Col
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        className="conditional-text-center">
                                                                                        <label>
                                                                                            Outer
                                                                                            Bound
                                                                                        </label>
                                                                                    </Col>
                                                                                    <Col
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        className="conditional-text-center">
                                                                                        <label>
                                                                                            Inner
                                                                                            Bound
                                                                                        </label>
                                                                                    </Col>
                                                                                    <Col
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        className="conditional-text-center">
                                                                                        <label>
                                                                                            Middle
                                                                                            of
                                                                                            range
                                                                                        </label>
                                                                                    </Col>
                                                                                </Row>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </FormGroup>
                                                                <div className="fee-section">
                                                                    <FormGroup>
                                                                        <Row className="center-items margin-bottom-1-rem">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Revenue
                                                                                    Multiple
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <Field
                                                                                    type="number"
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple"
                                                                                    value={
                                                                                        settings
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeRevenueMultiple
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) => {
                                                                                        let value =
                                                                                            Number(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            );

                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple =
                                                                                            value;
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                        <Row className="center-items">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Value
                                                                                    per
                                                                                    DVM
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplaceCurrencyField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeValuePerDvm
                                                                                    }
                                                                                    setFormValue={(
                                                                                        name: string,
                                                                                        value: string
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );

                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            {
                                                                                                ...newSettings,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                    </FormGroup>
                                                                </div>
                                                            </>
                                                        )}
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.FullServiceBrokerage) && (
                                                            <>
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Full
                                                                        Service
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <div className="fee-section">
                                                                    <FormGroup>
                                                                        <Row className="center-items margin-bottom-1-rem">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Revenue
                                                                                    Multiple
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <Field
                                                                                    type="number"
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple"
                                                                                    value={
                                                                                        settings
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeRevenueMultiple
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) => {
                                                                                        let value =
                                                                                            Number(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple =
                                                                                            value;
                                                                                        setSettings(
                                                                                            {
                                                                                                ...newSettings,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                        <Row className="center-items">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Value
                                                                                    per
                                                                                    DVM
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5">
                                                                                <MarketplaceCurrencyField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeValuePerDvm
                                                                                    }
                                                                                    setFormValue={(
                                                                                        name: string,
                                                                                        value: string
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );

                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            {
                                                                                                ...newSettings,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                    </FormGroup>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="transaction-fees-subsection">
                                                        <div className="mb-1">
                                                            <h6>
                                                                Transaction Fees
                                                            </h6>
                                                        </div>
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.DiscountBrokerage) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Discount
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <TransactionFeesTable
                                                                    brokerageType="discountBrokeragePricing"
                                                                    transactionFees={
                                                                        settings
                                                                            .discountBrokeragePricing
                                                                            .transactionFees
                                                                    }
                                                                    onChange={(
                                                                        newState: TransactionFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'discountBrokeragePricing',
                                                                            'transactionFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.FullServiceBrokerage) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Full
                                                                        Service
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <TransactionFeesTable
                                                                    brokerageType="fullServiceBrokeragePricing"
                                                                    transactionFees={
                                                                        settings
                                                                            .fullServiceBrokeragePricing
                                                                            .transactionFees
                                                                    }
                                                                    onChange={(
                                                                        newState: TransactionFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'fullServiceBrokeragePricing',
                                                                            'transactionFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="buyers-fees-subsection">
                                                        <div>
                                                            <h6>
                                                                Buyers' Fees
                                                            </h6>
                                                        </div>
                                                        <div className="fee-section">
                                                            <BuyersFeesTable
                                                                buyersFees={
                                                                    settings.buyersFees
                                                                }
                                                                onChange={(
                                                                    newState: BuyersFees
                                                                ) => {
                                                                    updateTable(
                                                                        undefined,
                                                                        'buyersFees',
                                                                        newState
                                                                    );
                                                                }}
                                                                setFormFieldValue={
                                                                    setFieldValue
                                                                }
                                                                formState={
                                                                    values
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <ButtonLoader
                                                    buttonText={'Save Updates'}
                                                    loaderButtonText={''}
                                                    disabled={false}
                                                    isLoading={isSaving}
                                                    onClick={initSubmit}
                                                    className="btn btn-primary"
                                                />
                                            </>
                                        );
                                    }}
                                </Form>
                            </div>
                        ) : (
                            Helpers.renderTableLoader()
                        )}
                    </div>
                </div>
            </div>
            {isConfirmationVisible && (
                <ConfirmationDialogWithReason
                    isVisible={isConfirmationVisible}
                    setIsVisible={setIsConfirmationVisible}
                    action={saveChanges}
                    reasonForAction={settings.reason}
                    setReasonForAction={(reason: string) => {
                        setSettings({
                            ...settings,
                            reason: reason,
                        });
                    }}
                    title={'Please specify reason for a settings change.'}
                    loader={isSaving}
                />
            )}
        </div>
    );
};

export default MarketplaceSettings;
